import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

type RevealTag = "div" | "li" | "section" | "article" | "header" | "figure";

type RevealProps = {
  children: ReactNode;
  className?: string;
  /** 表示開始を少しだけ遅らせたいときに使う（ミリ秒） */
  delay?: number;
  as?: RevealTag;
};

/**
 * スクロールで画面に入ったときに、下から少し移動しながらフェードインする。
 * 一度表示された要素は監視を解除するので、再スクロールで繰り返し再生されない。
 * prefers-reduced-motion が有効な環境ではアニメーションせず即表示する。
 */
export default function Reveal({ children, className = "", delay = 0, as = "div" }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const wantsReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (wantsReducedMotion || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -48px 0px" },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const Tag = as as "div";

  return (
    <Tag
      ref={ref}
      className={`${visible ? "reveal is-visible" : "reveal"} ${className}`.trim()}
      style={delay > 0 ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}